/**
 * Wall face-sketch tools — BIM-style vertical-profile editing.
 *
 * Both tools draw a closed polygon directly on the host wall's vertical
 * face (the sketch plane through the wall's location line) and commit in
 * the wall's LOCAL (u, v) frame — u along the wall run from its start,
 * v above the wall base — so sketches follow the wall when it moves:
 *
 *  - Edit Wall Shape Profile (CTX_EDIT_WALL_SHAPE): redefines the wall's
 *    whole outline (geometryData.elevationProfile); the body renders as the
 *    profile swept across the thickness (gable / shaped-top walls).
 *  - Sketch Wall Void (CTX_WALL_SKETCH_VOID): creates a 'Profile' Structural
 *    Opening element that CSG-subtracts from the host wall.
 *
 * Lifecycle mirrors PolygonSketchTool (tools/contextual/ElementEditTools.ts):
 * activate reads the selection, clicks build the loop (close near start or
 * Enter), Escape clears then exits, commit goes through bimStore so the
 * change is undoable, and control returns to SELECT keeping the selection.
 *
 * The sketch plane is meaningless when viewed edge-on (plan view): grazing
 * rays are rejected instead of producing wild (u, v) points — use a 3D or
 * elevation view for these tools.
 */
import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { v4 as uuidv4 } from 'uuid';
import { BaseTool } from '../BaseTool';
import { StructuralOpeningElement, WallElement } from '../../core/types';
import {
    WallFrameContext,
    polygonBounds2D,
    wallFaceNormal,
    wallLocalToWorld,
    worldToWallLocal,
} from '../../utils/wallProfileGeometry';
import { getSelectedElements } from '../../utils/contextualModelUtils';
import i18n from '../../i18n';

const SKETCH_SNAP = 0.05;          // meters, wall-local sketch grid
const SKETCH_CLOSE_TOLERANCE = 0.2; // meters, loop-close radius
const GRAZE_DOT = 0.02;             // min |ray . normal| for a usable plane hit

function snapMeters(value: number): number {
    return Math.round(value / SKETCH_SNAP) * SKETCH_SNAP;
}

// Module-level scratch objects: pointer projection runs at device rate, so
// per-event allocations would churn the GC (same pattern as BaseTool's).
const _ndc = new THREE.Vector2();
const _planeHit = new THREE.Vector3();

abstract class WallFaceSketchToolBase extends BaseTool {
    protected wall: WallElement | null = null;
    protected frame: WallFrameContext | null = null;
    protected points: [number, number][] = [];
    protected currentUV: [number, number] | null = null;

    /** Read the selection, lock onto a single wall and prepare the frame. */
    protected beginWallSketch(preload: [number, number][] | null, hint: string): boolean {
        const selected = getSelectedElements().filter(e => e.category === 'Walls') as WallElement[];
        if (selected.length !== 1) {
            this.exitToSelect(i18n.t('tools.notify.selectExactlyOneWallFirst'));
            return false;
        }
        const wall = selected[0];
        const start = new THREE.Vector3(...wall.geometryData.start);
        const end = new THREE.Vector3(...wall.geometryData.end);
        if (start.distanceTo(end) < 1e-6) {
            this.exitToSelect(i18n.t('tools.notify.thisWallHasADegenerateLocation'));
            return false;
        }
        this.wall = wall;
        this.frame = { start, end, baseZ: wall.geometryData.baseOffset || 0 };
        if (preload && preload.length >= 3) {
            this.points = preload.map(([u, v]) => [u, v] as [number, number]);
            this.updatePreview();
        } else if (hint) {
            this.notify(hint, 'info');
        }
        return true;
    }

    /**
     * Project the pointer onto the wall's vertical sketch plane and return
     * wall-local (u, v) meters. The ray is recomputed from the native pointer
     * position (BaseTool.getIntersection's rule) against the CURRENT camera,
     * and grazing rays (plan view) are rejected.
     */
    protected getSketchUV(event: ThreeEvent<PointerEvent>): [number, number] | null {
        if (!this.context || !this.frame) return null;
        const domElement = this.context.renderer.domElement;
        const rect = domElement.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return null;
        _ndc.set(
            ((event.nativeEvent.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.nativeEvent.clientY - rect.top) / rect.height) * 2 + 1
        );
        const camera = this.context.camera;
        camera.updateMatrixWorld();
        this.context.raycaster.setFromCamera(_ndc, camera);

        const normal = wallFaceNormal(this.frame);
        const mid = this.frame.start.clone().add(this.frame.end).multiplyScalar(0.5);
        const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, mid);
        if (Math.abs(this.context.raycaster.ray.direction.dot(normal)) < GRAZE_DOT) {
            return null;
        }
        const hit = this.context.raycaster.ray.intersectPlane(plane, _planeHit);
        if (!hit) return null;
        const { u, v } = worldToWallLocal(this.frame, _planeHit.clone());
        return [snapMeters(u), snapMeters(v)];
    }

    protected onSketchPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (event.button !== 0) return;
        const uv = this.getSketchUV(event);
        if (!uv) return;
        if (this.points.length > 2) {
            const [u0, v0] = this.points[0];
            if (Math.hypot(uv[0] - u0, uv[1] - v0) < SKETCH_CLOSE_TOLERANCE) {
                this.finish();
                return;
            }
        }
        this.points.push(uv);
        this.updatePreview();
    }

    protected onSketchPointerMove(event: ThreeEvent<PointerEvent>): void {
        const uv = this.getSketchUV(event);
        if (uv) {
            this.currentUV = uv;
            this.updatePreview();
        }
    }

    /** Rubber-band preview rendered as a world-space polygon on the wall face. */
    protected updatePreview(): void {
        if (!this.context || !this.frame || this.points.length === 0) return;
        const preview = this.currentUV ? [...this.points, this.currentUV] : [...this.points];
        const worldPoints = preview.map(([u, v]) => wallLocalToWorld(this.frame!, u, v));
        this.context.appStore.setToolPreview({
            type: 'polygon',
            points: worldPoints,
            closed: true,
            color: this.previewColor,
        });
    }

    protected abstract get previewColor(): string;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        this.onSketchPointerDown(event);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        this.onSketchPointerMove(event);
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            if (this.points.length >= 3) {
                this.finish();
                return;
            }
            this.notify(i18n.t('tools.notify.needAtLeast3PointsTo2'), 'warning');
            return;
        }
        if (event.key === 'Escape' && this.points.length > 0) {
            this.points = [];
            this.currentUV = null;
            this.context?.appStore.setToolPreview(null);
            return;
        }
        super.onKeyDown(event);
    }

    /** Commit the finished loop; called with >= 3 closed points. */
    protected abstract finish(): void;

    /** Fresh wall read by id — commits replace element identity. */
    protected currentWall(): WallElement | null {
        if (!this.context || !this.wall) return null;
        return (this.context.bimStore.project.elements.get(this.wall.id) as WallElement | undefined) ?? null;
    }

    protected exitToSelect(message?: string): void {
        if (message) this.notify(message, 'warning');
        this.context?.appStore.setActiveTool('select');
    }

    deactivate(): void {
        this.wall = null;
        this.frame = null;
        this.points = [];
        this.currentUV = null;
        super.deactivate();
    }
}

/* ==================== WALL: Edit Shape Profile ==================== */

/**
 * Edit Wall Shape Profile (BIM "Edit Profile"): redraw the wall's whole
 * outline on its vertical face. Preloads the current elevation profile or a
 * plain rectangle, so the usual flow is "drag the gable top into place".
 */
export class EditWallShapeProfileTool extends WallFaceSketchToolBase {
    id = 'CTX_EDIT_WALL_SHAPE';
    name = 'Edit Shape Profile';
    cursor = 'crosshair';

    protected get previewColor(): string { return '#00b4ff'; }

    activate(context: any): void {
        super.activate(context);
        if (!this.beginWallSketch(null, '')) return;
        const wall = this.wall!;
        const g = wall.geometryData;
        const existing = g.elevationProfile;
        const runLength = startToEndLength(g);
        const preload: [number, number][] = (Array.isArray(existing) && existing.length >= 3)
            ? existing.map(([u, v]) => [u, v] as [number, number])
            : [[0, 0], [runLength, 0], [runLength, g.height ?? 3], [0, g.height ?? 3]];
        this.points = preload;
        this.updatePreview();
        this.notify(i18n.t('tools.notify.sketchTheWallOutlineOnIts'), 'info');
    }

    protected finish(): void {
        if (!this.context) return;
        const wall = this.currentWall();
        if (!wall || !this.frame) { this.exitToSelect(i18n.t('tools.notify.wallNoLongerAvailable')); return; }
        const profile = this.points.map(([u, v]) => [Number(u.toFixed(4)), Number(v.toFixed(4))] as [number, number]);

        // Never let the nominal height lag behind a taller sketch: the height
        // parameter feeds schedules, constraints and the plan cut-span test.
        const topV = polygonBounds2D(profile).maxV;
        const nextHeight = Math.max(wall.geometryData.height ?? 3, topV);

        this.context.bimStore.updateElement(wall.id, {
            geometryData: {
                ...wall.geometryData,
                elevationProfile: profile,
                height: nextHeight,
            },
        } as any);
        this.notify(i18n.t('tools.notify.shapeProfileApplied', { wall: wall.name ?? 'wall', count: profile.length }), 'success');
        this.context.appStore.setToolPreview(null);
        this.exitToSelect();
    }
}

/** Plan length of a wall geometry bag (start -> end run). */
function startToEndLength(g: WallElement['geometryData']): number {
    return Math.hypot(g.end[0] - g.start[0], g.end[1] - g.start[1]);
}

/* ==================== WALL: Sketch Void ==================== */

/**
 * Sketch Wall Void: draw a free-form opening outline on the wall face and
 * commit it as a 'Profile' Structural Opening element. The host renderer
 * subtracts it via CSG; it stays selectable/deletable like any opening.
 */
export class WallSketchVoidTool extends WallFaceSketchToolBase {
    id = 'CTX_WALL_SKETCH_VOID';
    name = 'Sketch Wall Void';
    cursor = 'crosshair';

    protected get previewColor(): string { return '#ff6b6b'; }

    activate(context: any): void {
        super.activate(context);
        this.beginWallSketch(null, i18n.t('tools.notify.sketchTheVoidOutlineOnThe'));
    }

    protected finish(): void {
        if (!this.context || !this.frame) return;
        const wall = this.currentWall();
        if (!wall) { this.exitToSelect(i18n.t('tools.notify.wallNoLongerAvailable2')); return; }

        const profile = this.points.map(([u, v]) => [Number(u.toFixed(4)), Number(v.toFixed(4))] as [number, number]);
        const bounds = polygonBounds2D(profile);
        const width = bounds.maxU - bounds.minU;
        const height = bounds.maxV - bounds.minV;
        if (width < 0.05 || height < 0.05) {
            this.notify(i18n.t('tools.notify.theSketchedVoidIsTooSmall'), 'warning');
            this.points = [];
            this.currentUV = null;
            this.context.appStore.setToolPreview(null);
            return;
        }
        const center = wallLocalToWorld(this.frame, (bounds.minU + bounds.maxU) / 2, (bounds.minV + bounds.maxV) / 2);
        const thickness = wall.geometryData.thickness ?? 0.2;

        const element: StructuralOpeningElement = {
            id: uuidv4(),
            category: 'Structural Openings',
            type: 'Profile Opening',
            parameters: {
                'Opening Kind': 'Profile',
                'Width': `${(width * 1000).toFixed(0)} mm`,
                'Height': `${(height * 1000).toFixed(0)} mm`,
                'Depth': `${(thickness * 1000).toFixed(0)} mm`,
                'Host': `${wall.category} (${wall.type})`,
            },
            constraints: [{ type: 'HOST_CONTAINED' as any, targetId: wall.id, data: {} }],
            geometryData: {
                kind: 'Profile',
                hostId: wall.id,
                center: [center.x, center.y, center.z],
                width,
                height,
                depth: thickness,
                rotation: 0,
                profile,
            },
        };

        this.context.bimStore.addElement(element);
        this.notify(i18n.t('tools.notify.voidAdded', { wall: wall.name ?? 'wall' }), 'success');
        this.context.appStore.setToolPreview(null);
        this.exitToSelect();
    }
}
